// src/pages/PlatformUsers.js
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  employmentRoleLabel,
  employmentRoleOptionsForBusinessUnit,
  platformEmploymentRoleLabel,
} from "../utils/roleLabels";
import "./PlatformUsers.css";

function safeString(v) {
  return typeof v === "string" ? v : v == null ? "" : String(v);
}

function formatRole(r) {
  return platformEmploymentRoleLabel(r);
}

function normalizePlatformRole(v) {
  const r = String(v || "").trim().toUpperCase();
  if (r === "SUPER_ADMIN" || r === "ADMIN" || r === "SUPPORT") return r;
  return "";
}

function normalizeUserStatus(v) {
  const s = String(v || "").trim().toUpperCase();
  if (s === "ACTIVE") return "ACTIVE";
  if (s === "SUSPENDED") return "SUSPENDED";
  if (s === "TERMINATED") return "CLOSED";
  if (s === "CLOSED") return "CLOSED";
  return "ACTIVE";
}

function normalizeEmploymentRole(v) {
  const r = safeString(v).trim().toLowerCase().replace(/\s+/g, "_");
  const allowed = new Set([
    "manager",
    "dispatcher",
    "advisor",
    "tech",
    "foreman",
    "parts",
    "staff",
    "super_admin",
    "admin",
    "support",
  ]);
  return allowed.has(r) ? r : "manager";
}

function fmtDateTime(v) {
  const s = String(v || "").trim();
  if (!s) return "—";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleString();
}

function splitName(name) {
  const parts = safeString(name).trim().split(/\s+/).filter(Boolean);
  return {
    firstName: parts[0] || "",
    lastName: parts.slice(1).join(" "),
  };
}

function normalizeAppUser(row) {
  const r = row && typeof row === "object" ? row : {};
  return {
    id: String(r?.id ?? ""),
    email: safeString(r?.email).trim().toLowerCase(),
    name: safeString(r?.name),
    status: normalizeUserStatus(r?.status),
    hasPin: !!r?.hasPin,
    pinDisplay: safeString(r?.pinDisplay || r?.pin_display || "").trim(),
    createdAt: r?.createdAt ?? r?.created_at ?? null,
    updatedAt: r?.updatedAt ?? r?.updated_at ?? null,
  };
}

function normalizeBusinessUnit(row) {
  const r = row && typeof row === "object" ? row : {};
  return {
    id: String(r?.id ?? r?.business_unit_id ?? ""),
    groupName: safeString(r?.group_name ?? r?.groupName),
    storeName: safeString(r?.store_name ?? r?.storeName),
    storeCode: safeString(r?.store_code ?? r?.storeCode),
    status: safeString(r?.status ?? "ACTIVE").toUpperCase(),
  };
}

function normalizeEmployment(e) {
  const r = e && typeof e === "object" ? e : {};
  return {
    id: String(r?.id ?? ""),
    appUserId: String(r?.appUserId ?? r?.app_user_id ?? ""),
    businessUnitId: String(r?.businessUnitId ?? r?.business_unit_id ?? ""),
    storeName: safeString(r?.storeName ?? r?.store_name),
    storeCode: safeString(r?.storeCode ?? r?.store_code),
    groupName: safeString(r?.groupName ?? r?.group_name),
    jobRole: safeString(r?.role ?? r?.jobRole ?? r?.job_role),
    isActive:
      r?.isActive != null
        ? !!r.isActive
        : r?.is_active != null
          ? !!r.is_active
          : true,
    businessUnitStatus: safeString(
      r?.businessUnitStatus ?? r?.business_unit_status
    ),
    createdAt: r?.createdAt ?? r?.created_at ?? null,
    updatedAt: r?.updatedAt ?? r?.updated_at ?? null,
  };
}

function normalizePlatformUser(row) {
  const r = row && typeof row === "object" ? row : {};
  const split = splitName(r?.name);
  return {
    id: String(r?.userId ?? r?.id ?? r?.user_id ?? ""),
    appUserId: String(r?.appUserId ?? r?.app_user_id ?? ""),
    email: safeString(r?.email).trim().toLowerCase(),
    name: safeString(r?.name),
    firstName: split.firstName,
    lastName: split.lastName,
    status: normalizeUserStatus(r?.status || "ACTIVE"),
    hasPin: !!r?.hasPin,
    pinDisplay: safeString(r?.pinDisplay || r?.pin_display || "").trim(),
    platformRole: normalizePlatformRole(
      r?.platformRole ?? r?.platform_role ?? ""
    ),
    platformActive:
      r?.platformActive != null
        ? !!r.platformActive
        : r?.platform_active != null
          ? !!r.platform_active
          : false,
    createdAt: r?.createdAt ?? r?.created_at ?? null,
    lastLoginAt: r?.lastLoginAt ?? r?.last_login_at ?? null,
    employments: [],
  };
}

function statusLabel(status) {
  const s = normalizeUserStatus(status);
  if (s === "SUSPENDED") return "Suspended";
  if (s === "CLOSED") return "Closed";
  return "Active";
}

function statusTone(status) {
  const s = normalizeUserStatus(status);
  if (s === "SUSPENDED" || s === "CLOSED") return "danger";
  return "success";
}

function pinValue(user) {
  const pin = safeString(user?.pinDisplay).trim();
  if (pin) return pin;
  if (user?.hasPin) return "set";
  return "—";
}

function pinReadonlyValue(user) {
  const pin = safeString(user?.pinDisplay).trim();
  if (pin) return pin;
  if (user?.hasPin) return "PIN set";
  return "—";
}

function Pill({ children, title = "", tone = "default" }) {
  return (
    <span
      className={`ftu-pill ${tone !== "default" ? `ftu-pill--${tone}` : ""}`}
      title={title}
    >
      {children}
    </span>
  );
}

function ReadOnlyField({ label, children }) {
  return (
    <div>
      <div className="ft-label">{label}</div>
      <div className="ftu-readonly">{children}</div>
    </div>
  );
}

export default function PlatformUsers() {
  const nav = useNavigate();
  const auth = useAuth();
  const { apiGet, apiPost, apiPatch, apiDelete } = auth;

  const [users, setUsers] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [query, setQuery] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);

  const [newFirstName, setNewFirstName] = useState("");
  const [newLastName, setNewLastName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newRole, setNewRole] = useState("");

  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);

  const [editFirstName, setEditFirstName] = useState("");
  const [editLastName, setEditLastName] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editAppStatus, setEditAppStatus] = useState("ACTIVE");
  const [editPlatformRole, setEditPlatformRole] = useState("");
  const [editPlatformActive, setEditPlatformActive] = useState(true);

  const [showAddEmployment, setShowAddEmployment] = useState(false);
  const [employmentSaving, setEmploymentSaving] = useState(false);
  const [employmentBusinessUnitId, setEmploymentBusinessUnitId] = useState("");
  const [employmentRole, setEmploymentRole] = useState("manager");
  const [employmentIsActive, setEmploymentIsActive] = useState(true);

  const [showEditEmployment, setShowEditEmployment] = useState(false);
  const [editingEmployment, setEditingEmployment] = useState(null);
  const [editEmploymentRole, setEditEmploymentRole] = useState("manager");
  const [editEmploymentIsActive, setEditEmploymentIsActive] = useState(true);

  const myEmail = String(auth?.user?.email || "").trim().toLowerCase();

  async function refresh() {
    try {
      setLoading(true);
      setErr("");

      const [
        platformUsersRaw,
        appUsersRaw,
        employmentsRaw,
        businessUnitsRaw,
      ] = await Promise.all([
        apiGet("/api/platform/users"),
        apiGet("/api/platform/app-users"),
        apiGet("/api/platform/employments"),
        apiGet("/api/platform/business-units"),
      ]);

      const platformUsers = (Array.isArray(platformUsersRaw)
        ? platformUsersRaw
        : []
      ).map(normalizePlatformUser);

      const appUsers = (Array.isArray(appUsersRaw) ? appUsersRaw : []).map(
        normalizeAppUser
      );

      const employmentsArray = Array.isArray(employmentsRaw?.employments)
        ? employmentsRaw.employments
        : Array.isArray(employmentsRaw)
          ? employmentsRaw
          : [];

      const employments = employmentsArray.map(normalizeEmployment);

      const buRaw = Array.isArray(businessUnitsRaw?.businessUnits)
        ? businessUnitsRaw.businessUnits
        : Array.isArray(businessUnitsRaw)
          ? businessUnitsRaw
          : [];

      const buList = buRaw.map(normalizeBusinessUnit).sort((a, b) => {
        const aName =
          `${a.groupName} ${a.storeName} ${a.storeCode}`.toLowerCase();
        const bName =
          `${b.groupName} ${b.storeName} ${b.storeCode}`.toLowerCase();
        return aName.localeCompare(bName);
      });

      setBusinessUnits(buList);

      const appUserByEmail = new Map();
      for (const au of appUsers) {
        if (au.email) appUserByEmail.set(au.email, au);
      }

      const employmentsByAppUserId = new Map();
      for (const emp of employments) {
        const key = emp.appUserId;
        if (!key) continue;
        if (!employmentsByAppUserId.has(key)) {
          employmentsByAppUserId.set(key, []);
        }
        employmentsByAppUserId.get(key).push(emp);
      }

      const merged = platformUsers.map((pu) => {
        const au = appUserByEmail.get(pu.email) || null;
        const appUserId = pu.appUserId || au?.id || "";
        const mergedEmployments = appUserId
          ? employmentsByAppUserId.get(appUserId) || []
          : [];

        const mergedName = pu.name || au?.name || "";
        const split = splitName(mergedName);

        return {
          ...pu,
          name: mergedName,
          firstName: split.firstName,
          lastName: split.lastName,
          status: au?.status || pu.status || "ACTIVE",
          hasPin: au ? !!au.hasPin || !!pu.hasPin : !!pu.hasPin,
          pinDisplay: safeString(pu.pinDisplay || au?.pinDisplay || "").trim(),
          createdAt: pu.createdAt || au?.createdAt || null,
          appUserId,
          employments: mergedEmployments,
        };
      });

      setUsers(merged);

      setSelectedId((prev) => {
        if (!prev) return "";
        return merged.some((u) => u.id === prev) ? prev : "";
      });
    } catch (e) {
      setErr(e?.message || "Failed to fetch users");
      setUsers([]);
      setBusinessUnits([]);
      setSelectedId("");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    (async () => {
      await refresh();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedUser = useMemo(
    () => users.find((u) => u.id === selectedId) || null,
    [users, selectedId]
  );

  useEffect(() => {
    if (!selectedUser) {
      setEditMode(false);
      return;
    }

    setEditMode(false);
    setEditFirstName(selectedUser.firstName || "");
    setEditLastName(selectedUser.lastName || "");
    setEditEmail(selectedUser.email || "");
    setEditAppStatus(normalizeUserStatus(selectedUser.status));
    setEditPlatformRole(selectedUser.platformRole || "");
    setEditPlatformActive(!!selectedUser.platformActive);
  }, [selectedUser]);

  useEffect(() => {
    if (!showAddEmployment) return;
    setEmploymentBusinessUnitId("");
    setEmploymentRole("manager");
    setEmploymentIsActive(true);
  }, [showAddEmployment]);

  useEffect(() => {
    if (!editingEmployment) return;
    setEditEmploymentRole(normalizeEmploymentRole(editingEmployment.jobRole));
    setEditEmploymentIsActive(!!editingEmployment.isActive);
  }, [editingEmployment]);

  const filteredUsers = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users;

    return users.filter((u) => {
      const empText = Array.isArray(u.employments)
        ? u.employments
            .map(
              (e) => `${e.groupName} ${e.storeName} ${e.storeCode} ${e.jobRole}`
            )
            .join(" ")
        : "";

      return `${u.name} ${u.firstName} ${u.lastName} ${u.email} ${u.platformRole} ${u.status} ${u.pinDisplay} ${empText}`
        .toLowerCase()
        .includes(q);
    });
  }, [users, query]);

  const availableBusinessUnitsForSelectedUser = useMemo(() => {
    if (!selectedUser?.appUserId) return businessUnits;

    const existing = new Set(
      (selectedUser.employments || []).map((e) => String(e.businessUnitId))
    );

    return businessUnits.filter((bu) => !existing.has(String(bu.id)));
  }, [businessUnits, selectedUser]);

  const businessUnitById = useMemo(() => {
    return new Map((businessUnits || []).map((bu) => [String(bu.id), bu]));
  }, [businessUnits]);

  const selectedEmploymentBusinessUnit = useMemo(() => {
    return businessUnitById.get(String(employmentBusinessUnitId)) || null;
  }, [businessUnitById, employmentBusinessUnitId]);

  async function createUser() {
    try {
      setErr("");
      const firstName = newFirstName.trim();
      const lastName = newLastName.trim();
      const email = newEmail.trim().toLowerCase();
      const role = normalizePlatformRole(newRole);
      const name = `${firstName} ${lastName}`.trim();

      if (!firstName) throw new Error("Please enter a first name.");
      if (!lastName) throw new Error("Please enter a last name.");
      if (!email.includes("@")) throw new Error("Please enter a valid email.");

      setCreating(true);

      const payload = { name, email };
      if (role) payload.role = role;

      await apiPost("/api/platform/users", payload);
      await refresh();

      setNewFirstName("");
      setNewLastName("");
      setNewEmail("");
      setNewRole("");
      setShowCreate(false);
      setSelectedId("");
    } catch (e) {
      setErr(e?.message || "Failed creating user");
    } finally {
      setCreating(false);
    }
  }

  async function saveChanges() {
    if (!selectedUser) return;

    try {
      setErr("");

      const firstName = editFirstName.trim();
      const lastName = editLastName.trim();
      const name = `${firstName} ${lastName}`.trim();

      if (!firstName) {
        throw new Error("Please enter a first name.");
      }

      if (!lastName) {
        throw new Error("Please enter a last name.");
      }

      const payload = {
        name,
        email: editEmail.trim().toLowerCase(),
        status:
          normalizeUserStatus(editAppStatus) === "CLOSED"
            ? "TERMINATED"
            : normalizeUserStatus(editAppStatus),
        platformRole: editPlatformRole
          ? normalizePlatformRole(editPlatformRole)
          : "",
        platformActive: !!editPlatformActive,
      };

      if (!payload.email || !payload.email.includes("@")) {
        throw new Error("Please enter a valid email.");
      }

      setSaving(true);
      await apiPatch(`/api/platform/users/${selectedUser.id}`, payload);
      await refresh();
    } catch (e) {
      setErr(e?.message || "Failed saving changes");
    } finally {
      setSaving(false);
    }
  }

  async function deleteSelectedUser() {
    if (!selectedUser?.id) return;
    if (!apiDelete) {
      setErr("apiDelete is missing.");
      return;
    }

    const isMe = (selectedUser.email || "").toLowerCase() === myEmail;
    if (isMe) {
      setErr("You cannot close your own account.");
      return;
    }

    const ok = window.confirm(
      `Close this user account?

${selectedUser.name || "—"}
${
        selectedUser.email || "—"
      }

This will disable platform access and store employments, but preserve account, store, and historical records.`
    );
    if (!ok) return;

    try {
      setErr("");
      setSaving(true);
      await apiDelete(`/api/platform/users/${selectedUser.id}`);
      setSelectedId("");
      await refresh();
    } catch (e) {
      setErr(e?.message || "Failed closing user account");
    } finally {
      setSaving(false);
    }
  }

  async function createEmployment() {
    if (!selectedUser?.appUserId) {
      setErr("This user does not have an app user profile yet.");
      return;
    }

    try {
      setErr("");

      if (!employmentBusinessUnitId) {
        throw new Error("Please select a Business Unit.");
      }

      setEmploymentSaving(true);

      await apiPost("/api/platform/employments", {
        appUserId: selectedUser.appUserId,
        businessUnitId: employmentBusinessUnitId,
        role: normalizeEmploymentRole(employmentRole),
        isActive: !!employmentIsActive,
      });

      await refresh();
      setShowAddEmployment(false);
    } catch (e) {
      setErr(e?.message || "Failed to create employment");
    } finally {
      setEmploymentSaving(false);
    }
  }

  async function saveEmploymentChanges() {
    if (!editingEmployment?.id) return;

    try {
      setErr("");
      setEmploymentSaving(true);

      await apiPatch(`/api/platform/employments/${editingEmployment.id}`, {
        role: normalizeEmploymentRole(editEmploymentRole),
        isActive: !!editEmploymentIsActive,
      });

      await refresh();
      setShowEditEmployment(false);
      setEditingEmployment(null);
    } catch (e) {
      setErr(e?.message || "Failed to update employment");
    } finally {
      setEmploymentSaving(false);
    }
  }

  function openEditEmployment(emp) {
    setEditingEmployment(emp);
    setShowEditEmployment(true);
  }

  function cancelEdits() {
    if (!selectedUser) return;
    setEditMode(false);
    setEditFirstName(selectedUser.firstName || "");
    setEditLastName(selectedUser.lastName || "");
    setEditEmail(selectedUser.email || "");
    setEditAppStatus(normalizeUserStatus(selectedUser.status));
    setEditPlatformRole(selectedUser.platformRole || "");
    setEditPlatformActive(!!selectedUser.platformActive);
  }

  function handleSelectUser(userId) {
    setSelectedId((prev) => (prev === userId ? "" : userId));
  }

  function closeDetails() {
    setSelectedId("");
    setEditMode(false);
  }

  return (
    <div className="ft-page ftu-page">
      <div className="ftu-shell">
        <div className="ftu-pageTop">
          <div className="ftu-titleWrap">
            <h1 className="ft-title">Users</h1>
            <p className="ft-subtitle">
              Platform and Store Users
            </p>
          </div>

          <div className="ftu-pageActions">
            <button
              className="ft-btn ft-btn--primary"
              onClick={() => setShowCreate(true)}
            >
              + New User
            </button>
            <button className="ft-btn ft-btn--ghost" onClick={refresh}>
              Refresh
            </button>
          </div>
        </div>

        {err ? (
          <div className="ft-card ft-card--error">
            <div className="ft-errorText">{err}</div>
          </div>
        ) : null}

        <div className="ftu-workspace">
          <div className="ft-card ftu-searchCard">
            <input
              className="ft-input ftu-searchInput"
              placeholder="Search name, email, role, store, group..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>

          <div
            className="ftu-directoryLayout"
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(0, 1.55fr) minmax(360px, 440px)",
              gap: "16px",
              alignItems: "start",
            }}
          >
            <div className="ftu-mainColumn">
              <div className="ft-card ftu-directoryCard">
                <div className="ft-card__header">
                  <div className="ft-card__headerTitle">Directory</div>
                  <div className="ft-card__headerSub">
                    {loading ? "Loading..." : `${filteredUsers.length} shown`}
                  </div>
                </div>

                <div className="ft-card__body">
                  {loading ? (
                    <div className="ft-muted">Loading users...</div>
                  ) : filteredUsers.length === 0 ? (
                    <div className="ft-muted">No users found.</div>
                  ) : (
                    <div className="ftu-list">
                      {filteredUsers.map((u) => {
                        const activeRow = selectedId === u.id;
                        const isMe = (u.email || "").toLowerCase() === myEmail;

                        return (
                          <div key={u.id} className="ftu-rowBlock">
                            <button
                              type="button"
                              onClick={() => handleSelectUser(u.id)}
                              className={`ftu-row ${activeRow ? "is-active" : ""}`}
                              style={{
                                minHeight: 62,
                                borderRadius: 12,
                              }}
                              aria-expanded={activeRow}
                            >
                              <div
                                className="ftu-rowMain"
                                style={{
                                  width: "100%",
                                  maxWidth: "100%",
                                }}
                              >
                                <div className="ftu-rowIdentity">
                                  <div className="ftu-nameLine">
                                    <span className="ftu-name">
                                      {u.name || "—"}
                                    </span>
                                    {isMe ? (
                                      <span className="ftu-you">you</span>
                                    ) : null}
                                  </div>
                                  <div className="ftu-email">{u.email}</div>
                                </div>

                                <div className="ftu-rowPills">
                                  {u.platformRole ? (
                                    <Pill>{formatRole(u.platformRole)}</Pill>
                                  ) : null}
                                  <Pill tone={statusTone(u.status)}>
                                    {statusLabel(u.status)}
                                  </Pill>
                                  <Pill tone="muted">PIN {pinValue(u)}</Pill>
                                </div>
                              </div>
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div
              className="ftu-sideColumn"
              style={{
                position: "sticky",
                top: "84px",
              }}
            >
              {selectedUser ? (
                <div className="ft-card ftu-profileCard">
                  <div className="ft-card__header">
                    <div className="ft-card__headerTitle">Profile</div>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                      }}
                    >
                      <div className="ft-card__headerSub">Selected</div>
                      <button
                        type="button"
                        className="ft-btn ft-btn--ghost"
                        onClick={closeDetails}
                        style={{ padding: "8px 12px" }}
                      >
                        Close
                      </button>
                    </div>
                  </div>

                  <div className="ft-card__body">
                    <div className="ftu-profileHero">
                      <div className="ftu-profileHeroText">
                        <div className="ftu-profileName">
                          {selectedUser.name || "—"}
                        </div>
                        <div className="ftu-profileEmail">
                          {selectedUser.email || "—"}
                        </div>
                      </div>

                      <div className="ftu-profilePills">
                        {selectedUser.platformRole ? (
                          <Pill>{formatRole(selectedUser.platformRole)}</Pill>
                        ) : null}
                        <Pill
                          tone={
                            selectedUser.platformActive ? "success" : "danger"
                          }
                        >
                          {selectedUser.platformActive
                            ? "Platform Active"
                            : "Platform Inactive"}
                        </Pill>
                        <Pill tone={statusTone(selectedUser.status)}>
                          {statusLabel(selectedUser.status)}
                        </Pill>
                        <Pill tone="muted">
                          PIN {pinValue(selectedUser)}
                        </Pill>
                      </div>
                    </div>

                    <div className="ftu-section">
                      <div className="ftu-sectionTitle">Identity</div>

                      <div className="ftu-formGrid">
                        <div>
                          <div className="ft-label">First Name</div>
                          {editMode ? (
                            <input
                              className="ft-input"
                              value={editFirstName}
                              onChange={(e) => setEditFirstName(e.target.value)}
                            />
                          ) : (
                            <div className="ftu-readonly">
                              {selectedUser.firstName || "—"}
                            </div>
                          )}
                        </div>

                        <div>
                          <div className="ft-label">Last Name</div>
                          {editMode ? (
                            <input
                              className="ft-input"
                              value={editLastName}
                              onChange={(e) => setEditLastName(e.target.value)}
                            />
                          ) : (
                            <div className="ftu-readonly">
                              {selectedUser.lastName || "—"}
                            </div>
                          )}
                        </div>

                        <div className="ftu-span2">
                          <div className="ft-label">Email</div>
                          {editMode ? (
                            <input
                              className="ft-input"
                              value={editEmail}
                              onChange={(e) => setEditEmail(e.target.value)}
                            />
                          ) : (
                            <div className="ftu-readonly">
                              {selectedUser.email || "—"}
                            </div>
                          )}
                        </div>

                        <div>
                          <div className="ft-label">Status</div>
                          {editMode ? (
                            <select
                              className="ft-select"
                              value={editAppStatus}
                              onChange={(e) => setEditAppStatus(e.target.value)}
                            >
                              <option value="ACTIVE">ACTIVE</option>
                              <option value="SUSPENDED">SUSPENDED</option>
                              <option value="CLOSED">CLOSED</option>
                            </select>
                          ) : (
                            <div className="ftu-readonly">
                              {statusLabel(selectedUser.status)}
                            </div>
                          )}
                        </div>

                        <ReadOnlyField label="PIN">
                          {pinReadonlyValue(selectedUser)}
                        </ReadOnlyField>
                      </div>

                      <div className="ftu-metaGrid">
                        <div className="ftu-metaItem">
                          <div className="ftu-metaLabel">Created</div>
                          <div className="ftu-metaValue">
                            {fmtDateTime(selectedUser.createdAt)}
                          </div>
                        </div>

                        <div className="ftu-metaItem">
                          <div className="ftu-metaLabel">Last login</div>
                          <div className="ftu-metaValue">
                            {fmtDateTime(selectedUser.lastLoginAt)}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="ftu-section">
                      <div className="ftu-sectionTitle">
                        Special Platform Access
                      </div>

                      <div className="ftu-formGrid">
                        <div>
                          <div className="ft-label">Platform Role</div>
                          {editMode ? (
                            <select
                              className="ft-select"
                              value={editPlatformRole || "NONE"}
                              onChange={(e) =>
                                setEditPlatformRole(
                                  e.target.value === "NONE"
                                    ? ""
                                    : e.target.value
                                )
                              }
                            >
                              <option value="SUPER_ADMIN">SUPER_ADMIN</option>
                              <option value="ADMIN">ADMIN</option>
                              <option value="SUPPORT">SUPPORT</option>
                              <option value="NONE">
                                NONE (remove access)
                              </option>
                            </select>
                          ) : (
                            <div className="ftu-readonly">
                              {selectedUser.platformRole
                                ? formatRole(selectedUser.platformRole)
                                : "None"}
                            </div>
                          )}
                        </div>

                        <div>
                          <div className="ft-label">Platform Active</div>
                          {editMode ? (
                            <select
                              className="ft-select"
                              value={editPlatformActive ? "true" : "false"}
                              onChange={(e) =>
                                setEditPlatformActive(
                                  e.target.value === "true"
                                )
                              }
                            >
                              <option value="true">Active</option>
                              <option value="false">Inactive</option>
                            </select>
                          ) : (
                            <div className="ftu-readonly">
                              {selectedUser.platformActive
                                ? "Active"
                                : "Inactive"}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="ftu-hint">
                        Use platform privileges only for internal Flowtrak
                        admins/support. Normal store access should come from
                        employments.
                      </div>
                    </div>

                    <div className="ftu-section">
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 10,
                          marginBottom: 10,
                        }}
                      >
                        <div
                          className="ftu-sectionTitle"
                          style={{ marginBottom: 0 }}
                        >
                          Employments
                        </div>

                        <button
                          className="ft-btn ft-btn--ghost"
                          type="button"
                          onClick={() => setShowAddEmployment(true)}
                          disabled={!selectedUser.appUserId}
                        >
                          + Add Employment
                        </button>
                      </div>

                      {Array.isArray(selectedUser.employments) &&
                      selectedUser.employments.length ? (
                        <div className="ftu-employments">
                          {selectedUser.employments.map((emp) => (
                            <div
                              key={
                                emp.id ||
                                `${emp.appUserId}-${emp.businessUnitId}`
                              }
                              className="ftu-empCard"
                            >
                              <div className="ftu-empTop">
                                <div className="ftu-empStore">
                                  {emp.groupName ? `${emp.groupName} / ` : ""}
                                  {emp.storeName || "—"}
                                  {emp.storeCode ? (
                                    <span className="ftu-empCode">
                                      {emp.storeCode}
                                    </span>
                                  ) : null}
                                </div>
                                <div className="ftu-empRole">
                                  {emp.jobRole ? employmentRoleLabel(emp.jobRole, emp) : "—"}
                                </div>
                              </div>

                              <div className="ftu-empMeta">
                                <span>
                                  {emp.isActive
                                    ? "Status: Active"
                                    : "Status: Inactive"}
                                </span>
                                <span className="ftu-dot" />
                                <span>
                                  {emp.createdAt
                                    ? `Added: ${fmtDateTime(emp.createdAt)}`
                                    : "Added: —"}
                                </span>
                              </div>

                              <div
                                style={{
                                  marginTop: 10,
                                  display: "flex",
                                  gap: 10,
                                  flexWrap: "wrap",
                                }}
                              >
                                <button
                                  className="ft-btn ft-btn--ghost"
                                  type="button"
                                  onClick={() => openEditEmployment(emp)}
                                >
                                  Edit Employment
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="ft-muted">
                          No employments found for this user yet.
                        </div>
                      )}
                    </div>

                    <div className="ftu-footerBar">
                      {!editMode ? (
                        <button
                          className="ft-btn ft-btn--primary"
                          onClick={() => setEditMode(true)}
                        >
                          Edit
                        </button>
                      ) : (
                        <>
                          <button
                            className="ft-btn ft-btn--primary"
                            onClick={saveChanges}
                            disabled={saving}
                          >
                            {saving ? "Saving…" : "Save Changes"}
                          </button>
                          <button
                            className="ft-btn ft-btn--ghost"
                            onClick={cancelEdits}
                            disabled={saving}
                          >
                            Cancel
                          </button>
                        </>
                      )}

                      {(selectedUser.email || "").toLowerCase() === myEmail ? null : (
                        <button
                          className="ft-btn ft-btn--ghost"
                          onClick={async () => {
                            try {
                              setErr("");

                              if (!auth?.startImpersonation) {
                                throw new Error("Impersonation is not available yet.");
                              }

                              const data = await apiPost("/api/platform/impersonation/start", {
                                targetUserId: selectedUser.id,
                              });

                              if (!data?.impersonationToken) {
                                throw new Error("No impersonation token returned.");
                              }

                              await auth.startImpersonation(data.impersonationToken);
                            } catch (e) {
                              setErr(e?.message || "Failed to start impersonation");
                            }
                          }}
                          disabled={saving}
                        >
                          Impersonate
                        </button>
                      )}

                      {(selectedUser.email || "").toLowerCase() === myEmail ? null : (
                        <button
                          className="ft-btn ft-btn--ghost"
                          onClick={deleteSelectedUser}
                          disabled={saving}
                        >
                          Close Account
                        </button>
                      )}

                      <button
                        className="ft-btn ft-btn--ghost"
                        onClick={() => nav("/platform/admin")}
                        disabled={saving}
                      >
                        Back to Platform
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  className="ft-card"
                  style={{
                    height: 180,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "var(--muted)",
                    fontSize: 13,
                    borderStyle: "dashed",
                    opacity: 0.6,
                  }}
                >
                  Select a user to view details
                </div>
              )}
            </div>
          </div>
        </div>

        {showCreate ? (
          <div className="ft-modalOverlay" role="dialog" aria-modal="true">
            <div className="ft-modal">
              <div className="ft-modalHeader">
                <div>
                  <div className="ft-cardTitle">Create User</div>
                  <div className="ft-muted" style={{ padding: 0, fontSize: 12 }}>
                    Unified user directory
                  </div>
                </div>

                <button
                  type="button"
                  className="ft-btn ft-btn--ghost"
                  onClick={() => !creating && setShowCreate(false)}
                >
                  Close
                </button>
              </div>

              <div className="ft-modalBody">
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  <div>
                    <div className="ft-label">First Name</div>
                    <input
                      className="ft-input"
                      value={newFirstName}
                      onChange={(e) => setNewFirstName(e.target.value)}
                    />
                  </div>

                  <div>
                    <div className="ft-label">Last Name</div>
                    <input
                      className="ft-input"
                      value={newLastName}
                      onChange={(e) => setNewLastName(e.target.value)}
                    />
                  </div>
                </div>

                <div>
                  <div className="ft-label">Email</div>
                  <input
                    className="ft-input"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                  />
                </div>

                <div>
                  <div className="ft-label">
                    Special Platform Role (optional)
                  </div>
                  <select
                    className="ft-select"
                    value={newRole}
                    onChange={(e) => setNewRole(e.target.value)}
                  >
                    <option value="">NONE</option>
                    <option value="ADMIN">ADMIN</option>
                    <option value="SUPPORT">SUPPORT</option>
                    <option value="SUPER_ADMIN">SUPER_ADMIN</option>
                  </select>
                </div>
              </div>

              <div className="ft-modalFooter">
                <button
                  type="button"
                  className="ft-btn ft-btn--ghost"
                  onClick={() => setShowCreate(false)}
                  disabled={creating}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  className="ft-btn ft-btn--primary"
                  onClick={createUser}
                  disabled={creating}
                >
                  {creating ? "Creating…" : "Create User"}
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {showAddEmployment ? (
          <div className="ft-modalOverlay" role="dialog" aria-modal="true">
            <div className="ft-modal">
              <div className="ft-modalHeader">
                <div>
                  <div className="ft-cardTitle">Add Employment</div>
                  <div className="ft-muted" style={{ padding: 0, fontSize: 12 }}>
                    {selectedUser
                      ? `${selectedUser.name || "Selected User"} • ${
                          selectedUser.email || ""
                        }`
                      : ""}
                  </div>
                </div>

                <button
                  type="button"
                  className="ft-btn ft-btn--ghost"
                  onClick={() =>
                    !employmentSaving && setShowAddEmployment(false)
                  }
                >
                  Close
                </button>
              </div>

              <div className="ft-modalBody">
                <div>
                  <div className="ft-label">Business Unit</div>
                  <select
                    className="ft-select"
                    value={employmentBusinessUnitId}
                    onChange={(e) =>
                      setEmploymentBusinessUnitId(e.target.value)
                    }
                    disabled={employmentSaving}
                  >
                    <option value="">Select a Business Unit</option>
                    {availableBusinessUnitsForSelectedUser.map((bu) => (
                      <option key={bu.id} value={bu.id}>
                        {`${bu.groupName || "—"} / ${bu.storeName || "—"}${
                          bu.storeCode ? ` (${bu.storeCode})` : ""
                        }`}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <div className="ft-label">Role</div>
                  <select
                    className="ft-select"
                    value={employmentRole}
                    onChange={(e) => setEmploymentRole(e.target.value)}
                    disabled={employmentSaving}
                  >
                    {employmentRoleOptionsForBusinessUnit(
                      selectedEmploymentBusinessUnit
                    ).map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <div className="ft-label">Employment Status</div>
                  <select
                    className="ft-select"
                    value={employmentIsActive ? "true" : "false"}
                    onChange={(e) =>
                      setEmploymentIsActive(e.target.value === "true")
                    }
                    disabled={employmentSaving}
                  >
                    <option value="true">ACTIVE</option>
                    <option value="false">INACTIVE</option>
                  </select>
                </div>
              </div>

              <div className="ft-modalFooter">
                <button
                  type="button"
                  className="ft-btn ft-btn--ghost"
                  onClick={() => setShowAddEmployment(false)}
                  disabled={employmentSaving}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  className="ft-btn ft-btn--primary"
                  onClick={createEmployment}
                  disabled={employmentSaving || !employmentBusinessUnitId}
                >
                  {employmentSaving ? "Adding…" : "Add Employment"}
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {showEditEmployment ? (
          <div className="ft-modalOverlay" role="dialog" aria-modal="true">
            <div className="ft-modal">
              <div className="ft-modalHeader">
                <div>
                  <div className="ft-cardTitle">Edit Employment</div>
                  <div className="ft-muted" style={{ padding: 0, fontSize: 12 }}>
                    {editingEmployment
                      ? `${
                          editingEmployment.groupName
                            ? `${editingEmployment.groupName} / `
                            : ""
                        }${editingEmployment.storeName || ""}${
                          editingEmployment.storeCode
                            ? ` (${editingEmployment.storeCode})`
                            : ""
                        }`
                      : ""}
                  </div>
                </div>

                <button
                  type="button"
                  className="ft-btn ft-btn--ghost"
                  onClick={() => {
                    if (!employmentSaving) {
                      setShowEditEmployment(false);
                      setEditingEmployment(null);
                    }
                  }}
                >
                  Close
                </button>
              </div>

              <div className="ft-modalBody">
                <div>
                  <div className="ft-label">Role</div>
                  <select
                    className="ft-select"
                    value={editEmploymentRole}
                    onChange={(e) => setEditEmploymentRole(e.target.value)}
                    disabled={employmentSaving}
                  >
                    {employmentRoleOptionsForBusinessUnit(editingEmployment).map(
                      (option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      )
                    )}
                  </select>
                </div>

                <div>
                  <div className="ft-label">Employment Status</div>
                  <select
                    className="ft-select"
                    value={editEmploymentIsActive ? "true" : "false"}
                    onChange={(e) =>
                      setEditEmploymentIsActive(e.target.value === "true")
                    }
                    disabled={employmentSaving}
                  >
                    <option value="true">ACTIVE</option>
                    <option value="false">INACTIVE</option>
                  </select>
                </div>
              </div>

              <div className="ft-modalFooter">
                <button
                  type="button"
                  className="ft-btn ft-btn--ghost"
                  onClick={() => {
                    setShowEditEmployment(false);
                    setEditingEmployment(null);
                  }}
                  disabled={employmentSaving}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  className="ft-btn ft-btn--primary"
                  onClick={saveEmploymentChanges}
                  disabled={employmentSaving}
                >
                  {employmentSaving ? "Saving…" : "Save Employment"}
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
